/**
 * UI Slice
 * Manages UI-related state (loading, modals, toasts, etc.)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UIState } from '@/types/store.types';

/**
 * Initial state
 */
const initialState: UIState = {
  isLoading: false,
  loadingMessage: undefined,
  modal: {
    isOpen: false,
    type: undefined,
    data: undefined,
  },
  toast: {
    show: false,
    message: undefined,
    type: undefined,
  },
  sidebar: {
    isOpen: true,
  },
};

/**
 * UI Slice
 */
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Set global loading state
     */
    setLoading: (
      state,
      action: PayloadAction<{ isLoading: boolean; message?: string }>
    ) => {
      state.isLoading = action.payload.isLoading;
      state.loadingMessage = action.payload.message;
    },
    
    /**
     * Show loading
     */
    showLoading: (state, action: PayloadAction<string | undefined>) => {
      state.isLoading = true;
      state.loadingMessage = action.payload;
    },
    
    /**
     * Hide loading
     */
    hideLoading: (state) => {
      state.isLoading = false;
      state.loadingMessage = undefined;
    },
    
    /**
     * Open modal
     */
    openModal: (
      state,
      action: PayloadAction<{ type: string; data?: unknown }>
    ) => {
      state.modal.isOpen = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data;
    },
    
    /**
     * Close modal
     */
    closeModal: (state) => {
      state.modal.isOpen = false;
      state.modal.type = undefined;
      state.modal.data = undefined;
    },
    
    /**
     * Show toast
     */
    showToast: (
      state,
      action: PayloadAction<{
        message: string;
        type?: 'success' | 'error' | 'info' | 'warning';
      }>
    ) => {
      state.toast.show = true;
      state.toast.message = action.payload.message;
      state.toast.type = action.payload.type || 'info';
    },
    
    /**
     * Hide toast
     */
    hideToast: (state) => {
      state.toast.show = false;
      state.toast.message = undefined;
      state.toast.type = undefined;
    },
    
    /**
     * Toggle sidebar
     */
    toggleSidebar: (state) => {
      state.sidebar.isOpen = !state.sidebar.isOpen;
    },
    
    /**
     * Set sidebar state
     */
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isOpen = action.payload;
    },
    
    /**
     * Reset UI state
     */
    resetUI: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setLoading,
  showLoading,
  hideLoading,
  openModal,
  closeModal,
  showToast,
  hideToast,
  toggleSidebar,
  setSidebarOpen,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
